import { createContext, useContext, useReducer, useRef, useState } from 'react';
import { getActiveAccountMasterId } from '../../../selfservice/common/localStorageUtils.js';
import { useAuth } from '../AuthProvider';
import { onlineModelReducer } from './onlineModelReducer';
import {
  batchQueryPrivateOnlineModelHeaders,
  fetchPrivateOnlineModels,
  filterByOnlineModelCodes,
  queryPublicOnlineModels,
  queryVoucherModels,
} from './onlineModelService';
import type { OnlineModelsState } from './onlineModelReducer';
import type { OnlineModel } from '../../../generated/api/onlineModel';
import type { ReactNode } from 'react';

/** Auth context a code was fetched in: private (authenticated) pricing vs public pricing. */
type FetchAuthState = 'private' | 'public';

interface RequestedCodes {
  /** Model codes already requested, mapped to the auth state they were fetched in (for dedup). */
  models: Map<string, FetchAuthState>;
  /** Header codes already requested for private pricing (for dedup). */
  headers: Set<string>;
  /** Model codes already requested per voucher code (for dedup of voucher-priced fetches). */
  voucherModels: Map<string, Set<string>>;
}

export interface OnlineModelContextData {
  priceAlteredModels: OnlineModelsState;
  /** Fetches full models in parallel — use for product details, cart, checkout (1-10 codes) */
  fetchModelsByCodes: (onlineModelCodes: string[]) => void;
  /** Queues + batches header fetches — use for category lists (50-2000 codes) */
  fetchHeadersByCodes: (onlineModelCodes: string[]) => void;
  setActiveVoucher: (voucherCode: string) => void;
  loadVoucherModels: (voucherCode: string, onlineModelCodeGuids: string[]) => Promise<{ models: OnlineModel[] }>;
  activeVoucherCode?: string;
  areModelCodesResolved: (codes: string[]) => boolean;
  isHeaderCodeResolved: (code: string) => boolean;
}

const OnlineModelContext = createContext<OnlineModelContextData>({
  priceAlteredModels: {},
  fetchModelsByCodes: () => {},
  fetchHeadersByCodes: () => {},
  setActiveVoucher: () => {},
  loadVoucherModels: async () => ({ models: [] }),
  activeVoucherCode: undefined,
  areModelCodesResolved: () => false,
  isHeaderCodeResolved: () => false,
});
export const OnlineModelContextProvider = ({
  children,
  initialData,
}: {
  children: ReactNode;
  initialData: OnlineModelsState;
}) => {
  const [onlineModelData, dispatch] = useReducer(onlineModelReducer, initialData);
  const [activeVoucherCode, setActiveVoucherCode] = useState<string>('');
  const { authenticatedUser } = useAuth();
  const settledMissingHeaderCodes = useRef<Set<string>>(new Set<string>());

  const requested = useRef<RequestedCodes>({
    models: new Map<string, FetchAuthState>(),
    headers: new Set<string>(Object.keys(initialData.onlineModelHeadersByCode ?? {})),
    voucherModels: new Map<string, Set<string>>(),
  });

  const setVoucher = (voucher: string) => {
    setActiveVoucherCode(voucher);
  };

  /**
   * Fetch full online models in parallel
   */
  const fetchModelsByCodes = (onlineModelCodes: string[]) => {
    const mdmId = authenticatedUser?.activeAccountMasterId ?? getActiveAccountMasterId();
    const authState: FetchAuthState = mdmId ? 'private' : 'public';
    // Refetch when a code was never requested, or was fetched in a different auth state.
    const codesToFetch = onlineModelCodes.filter(code => code && requested.current.models.get(code) !== authState);
    if (codesToFetch.length === 0) {
      return;
    }

    codesToFetch.forEach(code => requested.current.models.set(code, authState));

    const removeRequested = () => codesToFetch.forEach(code => requested.current.models.delete(code));

    if (mdmId) {
      fetchPrivateOnlineModels(codesToFetch, dispatch, mdmId, activeVoucherCode || undefined).catch(removeRequested);
    } else {
      queryPublicOnlineModels(codesToFetch, dispatch).catch(removeRequested);
    }
  };

  /**
   * Fetch headers in batched requests (50 per request).
   * Always fetches base customer prices — voucher pricing is not applied to category lists.
   */
  const fetchHeadersByCodes = (onlineModelCodes: string[]) => {
    const mdmId = authenticatedUser?.activeAccountMasterId ?? getActiveAccountMasterId();
    if (!mdmId) {
      return;
    }

    const codesToFetch = [...new Set(onlineModelCodes)].filter(code => code && !requested.current.headers.has(code));
    if (codesToFetch.length === 0) {
      return;
    }

    codesToFetch.forEach(code => requested.current.headers.add(code));

    batchQueryPrivateOnlineModelHeaders(codesToFetch, dispatch, mdmId)
      .then(results => {
        results.forEach(({ onlineModelCodes: requestedBatchCodes, headers }) => {
          const returnedHeaderCodes = new Set(headers.map(header => header.onlineModelCode));

          requestedBatchCodes.forEach(code => {
            if (!returnedHeaderCodes.has(code)) {
              settledMissingHeaderCodes.current.add(code);
            }
          });
        });
      })
      .catch(() => {
        codesToFetch.forEach(code => requested.current.headers.delete(code));
      });
  };

  const loadVoucherModels = async (voucherCode: string, onlineModelCodeGuids: string[]) => {
    const mdmId = authenticatedUser?.activeAccountMasterId ?? getActiveAccountMasterId();
    const requestedForVoucher = requested.current.voucherModels.get(voucherCode) ?? new Set<string>();

    // Only fetch codes not yet requested for this voucher — mirrors fetchModelsByCodes' dedup. Stops the
    // basket-write that follows applying a voucher from re-hitting the v3 private API for the same voucher,
    // and makes re-clicks fetch only newly added models.
    const codesToFetch = onlineModelCodeGuids.filter(code => code && !requestedForVoucher.has(code));

    // Voucher-priced models already cached for the requested codes (the source of truth for the result).
    const cachedModels = filterByOnlineModelCodes(
      onlineModelData.onlineModelsByVoucher?.[voucherCode],
      onlineModelCodeGuids
    );

    if (codesToFetch.length === 0) {
      return { models: cachedModels };
    }

    codesToFetch.forEach(code => requestedForVoucher.add(code));
    requested.current.voucherModels.set(voucherCode, requestedForVoucher);

    try {
      const { models } = await queryVoucherModels(voucherCode, codesToFetch, dispatch, mdmId);
      // Newly fetched codes are disjoint from cachedModels: anything cached was already in
      // requestedForVoucher, which codesToFetch excludes — so concat without re-deduping.
      return { models: [...cachedModels, ...models] };
    } catch (err) {
      // Roll back so a failed (e.g. invalid) voucher fetch can be retried.
      codesToFetch.forEach(code => requestedForVoucher.delete(code));
      throw err;
    }
  };

  const areModelCodesResolved = (codes: string[]) => {
    const loaded = onlineModelData.onlineModelsByCode ?? {};
    return codes.every(code => code in loaded);
  };

  const isHeaderCodeResolved = (code: string) => {
    const loaded = onlineModelData.onlineModelHeadersByCode ?? {};
    return code in loaded || settledMissingHeaderCodes.current.has(code);
  };

  const contextData: OnlineModelContextData = {
    priceAlteredModels: onlineModelData,
    fetchModelsByCodes,
    fetchHeadersByCodes,
    setActiveVoucher: setVoucher,
    activeVoucherCode,
    loadVoucherModels,
    areModelCodesResolved,
    isHeaderCodeResolved,
  };

  return <OnlineModelContext.Provider value={contextData}>{children}</OnlineModelContext.Provider>;
};

export const useOnlineModelsContext = (): OnlineModelContextData => useContext(OnlineModelContext);
